import { DatePicker, Select, Spin } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MobileEmpty } from '@/components/mobile/MobileEmpty';
import { MobileReportCard } from '@/components/mobile/MobileReportCard';
import { SubordinateStatusCard } from '@/components/mobile/SubordinateStatusCard';
import { ROUTES } from '@/constants/routes';
import { useLeaderTeamSnapshot } from '@/hooks/useLeaderTeamSnapshot';
import * as departmentsApi from '@/services/api/departments';
import { flattenDepartmentTree } from '@/utils/department-tree';
import {
  filterSubordinateRows,
  type TeamSubmitFilter,
} from '@/utils/leader-team';
import { getLocalDateKey } from '@/utils/date';

const SUBMIT_FILTER_OPTIONS = [
  { value: 'all', label: '全部' },
  { value: 'submitted', label: '已交' },
  { value: 'not_submitted', label: '未交' },
] as const;

export function TeamReportsMobilePage() {
  const navigate = useNavigate();
  const [reportDate, setReportDate] = useState<Dayjs>(() => dayjs());
  const [submitFilter, setSubmitFilter] = useState<TeamSubmitFilter>('all');
  const [userId, setUserId] = useState<string | undefined>();

  const { loading, subordinates, rows, dateKey, reload } = useLeaderTeamSnapshot(reportDate);
  const [departmentNameMap, setDepartmentNameMap] = useState<Map<string, string>>(new Map());

  const loadDepartmentNames = useCallback(async () => {
    try {
      const tree = await departmentsApi.fetchDepartmentTree();
      const map = new Map<string, string>();
      for (const dept of flattenDepartmentTree(tree)) {
        map.set(dept.id, dept.name);
      }
      setDepartmentNameMap(map);
    } catch {
      setDepartmentNameMap(new Map());
    }
  }, []);

  useEffect(() => {
    void loadDepartmentNames();
  }, [loadDepartmentNames]);

  const subordinateOptions = useMemo(
    () => subordinates.map((user) => ({ value: user.id, label: user.name })),
    [subordinates],
  );

  const filteredRows = useMemo(
    () => filterSubordinateRows(rows, { submitFilter, userId }),
    [rows, submitFilter, userId],
  );

  const openDetail = (reportId: string) => {
    navigate(ROUTES.mobile.leader.teamReportDetail(reportId));
  };

  return (
    <div>
      <div className="mobile-filter-panel">
        <div className="mobile-filter-row">
          <span className="mobile-filter-label">状态</span>
          <Select
            style={{ flex: 1 }}
            value={submitFilter}
            onChange={(value) => setSubmitFilter(value)}
            options={SUBMIT_FILTER_OPTIONS.map((item) => ({
              value: item.value,
              label: item.label,
            }))}
          />
        </div>
        <div className="mobile-filter-row">
          <span className="mobile-filter-label">员工</span>
          <Select
            allowClear
            placeholder="全部下属"
            style={{ flex: 1 }}
            value={userId}
            onChange={(value) => setUserId(value)}
            options={subordinateOptions}
          />
        </div>
        <div className="mobile-filter-row">
          <span className="mobile-filter-label">日期</span>
          <DatePicker
            allowClear={false}
            value={reportDate}
            onChange={(value) => value && setReportDate(value)}
            inputReadOnly
            style={{ flex: 1 }}
          />
        </div>
        <button type="button" className="mobile-btn mobile-btn--primary mobile-btn--block" onClick={() => void reload()}>
          查询
        </button>
      </div>

      {subordinates.length === 0 ? (
        <MobileEmpty message="暂无直属下属。请确认下属的部门与直属领导配置。" />
      ) : loading ? (
        <div style={{ textAlign: 'center', padding: 32 }}>
          <Spin />
        </div>
      ) : filteredRows.length === 0 ? (
        <MobileEmpty
          message={
            dateKey === getLocalDateKey()
              ? '当前筛选条件下暂无数据'
              : '该日期与筛选条件下暂无数据'
          }
        />
      ) : (
        filteredRows.map((row) =>
          row.submitted && row.report ? (
            <MobileReportCard
              key={row.user.id}
              report={row.report}
              subtitle={departmentNameMap.get(row.user.departmentId)}
              onClick={() => openDetail(row.report!.id)}
            />
          ) : (
            <SubordinateStatusCard
              key={row.user.id}
              row={row}
              departmentName={departmentNameMap.get(row.user.departmentId)}
            />
          ),
        )
      )}
    </div>
  );
}
